'use client';
import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { getFlag, type LiveFixture } from '@/lib/api-football';
import { GitBranch, RefreshCw } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

// API-Football league/season constants
const API_BASE = '/api/bracket-fixtures';

interface BracketMatch {
  id: number;
  round: string;
  home: { name: string; logo: string; score?: number } | null;
  away: { name: string; logo: string; score?: number } | null;
  winner?: string;
  date?: string;
  venue?: string;
  status: 'played' | 'upcoming' | 'tbd';
}

function MatchSlot({ match, compact = false }: { match: BracketMatch; compact?: boolean }) {
  const isPlayed = match.status === 'played';
  const isTBD = match.status === 'tbd';

  return (
    <div
      className={`rounded-xl overflow-hidden transition-all duration-200 hover:scale-[1.02] ${compact ? 'min-w-[160px]' : 'min-w-[200px]'}`}
      style={{
        background: isPlayed ? 'hsl(222 35% 13%)' : isTBD ? 'hsl(222 35% 10%)' : 'hsl(222 35% 12%)',
        border: `1px solid ${isPlayed ? 'hsl(222 25% 20%)' : 'hsl(222 25% 14%)'}`,
      }}
    >
      <div className="px-3 py-1.5 flex items-center justify-between" style={{ borderBottom: '1px solid hsl(222 25% 14%)', background: 'hsl(222 35% 11%)' }}>
        <span className="text-xs text-slate-500">{match.date ?? 'TBD'}</span>
        <span className="text-xs text-slate-600 truncate max-w-[100px]">{match.venue ?? ''}</span>
      </div>

      <div className="p-2 space-y-1">
        {isTBD ? (
          <>
            <div className="flex items-center justify-between px-1 py-1.5 rounded-lg" style={{ background: 'hsl(222 25% 14%)' }}>
              <span className="text-xs text-slate-600">TBD</span>
              <span className="text-xs text-slate-700">–</span>
            </div>
            <div className="flex items-center justify-between px-1 py-1.5 rounded-lg" style={{ background: 'hsl(222 25% 14%)' }}>
              <span className="text-xs text-slate-600">TBD</span>
              <span className="text-xs text-slate-700">–</span>
            </div>
          </>
        ) : (
          <>
            {[match.home, match.away].map((team, i) => {
              if (!team) return (
                <div key={i} className="flex items-center justify-between px-1 py-1.5 rounded-lg" style={{ background: 'hsl(222 25% 14%)' }}>
                  <span className="text-xs text-slate-600">TBD</span>
                </div>
              );
              const isWinner = match.winner === team.name;
              return (
                <div
                  key={i}
                  className="flex items-center justify-between px-2 py-1.5 rounded-lg"
                  style={{
                    background: isWinner ? 'hsl(25 95% 53% / 0.12)' : 'hsl(222 25% 14%)',
                    border: isWinner ? '1px solid hsl(25 95% 53% / 0.2)' : '1px solid transparent',
                  }}
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    {team.logo ? (
                      <img src={team.logo} alt={team.name} className="w-4 h-4 object-contain flex-shrink-0" />
                    ) : (
                      <span className="text-sm flex-shrink-0">{getFlag(team.name)}</span>
                    )}
                    <span className={`text-xs font-medium truncate ${isWinner ? 'text-white' : 'text-slate-400'}`}>{team.name}</span>
                  </div>
                  {team.score !== undefined && (
                    <span className={`text-sm font-bold font-mono ml-2 flex-shrink-0 ${isWinner ? 'text-orange-400' : 'text-slate-500'}`}>{team.score}</span>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

function fixtureToMatch(f: LiveFixture, round: string): BracketMatch {
  const isFinished = ['FT', 'AET', 'PEN'].includes(f.fixture.status.short);
  const isNS = f.fixture.status.short === 'NS';
  const homeScore = f.goals.home ?? undefined;
  const awayScore = f.goals.away ?? undefined;

  let winner: string | undefined;
  if (isFinished && homeScore !== undefined && awayScore !== undefined) {
    if (homeScore > awayScore) winner = f.teams.home.name;
    else if (awayScore > homeScore) winner = f.teams.away.name;
  }

  return {
    id: f.fixture.id,
    round,
    home: { name: f.teams.home.name, logo: f.teams.home.logo, score: homeScore },
    away: { name: f.teams.away.name, logo: f.teams.away.logo, score: awayScore },
    winner,
    date: new Date(f.fixture.date).toLocaleDateString([], { month: 'short', day: 'numeric' }),
    venue: f.fixture.venue.name ?? undefined,
    status: isFinished ? 'played' : isNS ? 'upcoming' : 'upcoming',
  };
}

async function fetchKnockoutFixtures(): Promise<BracketMatch[]> {
  try {
    const res = await fetch('/api/bracket-fixtures');
    if (!res.ok) return [];
    const data: LiveFixture[] = await res.json();
    return data.map(f => {
      const round = f.league.round ?? '';
      return fixtureToMatch(f, round);
    });
  } catch {
    return [];
  }
}

export default function KnockoutBracketPage() {
  const [view, setView] = useState<'bracket' | 'list'>('bracket');
  const [matches, setMatches] = useState<BracketMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<string>('');
  const { t } = useLanguage();

  const load = useCallback(async () => {
    const data = await fetchKnockoutFixtures();
    setMatches(data);
    setLoading(false);
    setLastUpdate(new Date().toLocaleTimeString());
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [load]);

  const r16 = matches.filter(m => m.round.toLowerCase().includes('16') || m.round.toLowerCase().includes('round of 16'));
  const qf = matches.filter(m => m.round.toLowerCase().includes('quarter'));
  const sf = matches.filter(m => m.round.toLowerCase().includes('semi'));
  const final = matches.filter(m => m.round.toLowerCase() === 'final' || m.round.toLowerCase().includes('world cup final'));

  const allRounds = [
    { key: t.bracket.roundOf16, matches: r16 },
    { key: t.bracket.quarterFinals, matches: qf },
    { key: t.bracket.semiFinals, matches: sf },
    { key: t.bracket.final, matches: final },
  ].filter(r => r.matches.length > 0);

  return (
    <AppLayout activeRoute="/bracket">
      <div className="space-y-6 pb-20 lg:pb-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
              <GitBranch size={22} className="text-orange-400" />
              {t.bracket.title}
            </h1>
            <p className="text-sm text-slate-400 mt-0.5">{t.bracket.subtitle}</p>
          </div>
          <div className="flex items-center gap-3">
            {lastUpdate && (
              <span className="flex items-center gap-1 text-xs text-slate-600">
                <RefreshCw size={10} className="text-cyan-500" />
                <span className="font-mono">{lastUpdate}</span>
              </span>
            )}
            <div className="flex items-center gap-2">
              {(['bracket', 'list'] as const).map(v => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${view === v ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-white'}`}
                  style={view !== v ? { background: 'hsl(222 35% 13%)' } : {}}
                >
                  {v === 'bracket' ? '⚡ Bracket' : '📋 List'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2].map(i => (
              <div key={i} className="rounded-2xl animate-pulse" style={{ background: 'hsl(222 35% 11%)', height: 200 }} />
            ))}
          </div>
        ) : matches.length === 0 ? (
          <div className="rounded-2xl p-12 text-center" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
            <div className="text-4xl mb-3">🏆</div>
            <p className="text-slate-300 font-semibold">{t.bracket.noData}</p>
            <p className="text-slate-500 text-sm mt-1">{t.bracket.noDataSub}</p>
          </div>
        ) : (
          <>
            {/* Progress Banner */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { label: t.bracket.roundOf16, total: 8, played: r16.filter(m => m.status === 'played').length, color: 'hsl(189 94% 51%)' },
                { label: t.bracket.quarterFinals, total: 4, played: qf.filter(m => m.status === 'played').length, color: 'hsl(25 95% 53%)' },
                { label: t.bracket.semiFinals, total: 2, played: sf.filter(m => m.status === 'played').length, color: 'hsl(280 84% 60%)' },
                { label: t.bracket.final, total: 1, played: final.filter(m => m.status === 'played').length, color: 'hsl(45 93% 47%)' },
              ].map(stage => (
                <div key={stage.label} className="rounded-xl p-3" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
                  <div className="text-xs text-slate-500 mb-1">{stage.label}</div>
                  <div className="text-lg font-black text-white">{stage.played}/{stage.total}</div>
                  <div className="mt-1.5 h-1 rounded-full overflow-hidden" style={{ background: 'hsl(222 25% 18%)' }}>
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(stage.played / stage.total) * 100}%`, background: stage.color }} />
                  </div>
                </div>
              ))}
            </div>

            {view === 'bracket' ? (
              <div className="overflow-x-auto pb-4">
                <div className="flex gap-6 min-w-max">
                  {allRounds.map((round, idx) => (
                    <React.Fragment key={round.key}>
                      {idx > 0 && (
                        <div className="flex items-center">
                          <div className="w-6 h-px" style={{ background: 'hsl(222 25% 20%)' }} />
                        </div>
                      )}
                      <div className="flex flex-col gap-3">
                        <div className={`text-xs font-bold uppercase tracking-widest px-1 mb-1 ${round.key === t.bracket.final ? 'text-orange-400' : 'text-slate-500'}`}>
                          {round.key === t.bracket.final ? `🏆 ${t.bracket.final}` : round.key}
                        </div>
                        {round.matches.map(m => (
                          <MatchSlot key={m.id} match={m} compact={round.key !== t.bracket.final} />
                        ))}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {allRounds.map(round => (
                  <div key={round.key}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="h-px flex-1" style={{ background: 'hsl(222 25% 18%)' }} />
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-widest px-3">{round.key}</span>
                      <div className="h-px flex-1" style={{ background: 'hsl(222 25% 18%)' }} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                      {round.matches.map(m => <MatchSlot key={m.id} match={m} />)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
